import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SiteDialog from '../../components/SiteDialog';
import { useAppSelector } from '../../hooks';
import { CarouselType } from '../../types/types';
import { useLocation } from 'react-router-dom';
import Loading from '../../components/Loading';
import { useCarouselRequest } from '../../hooks/carouselRequest';
import CreateCarousel from '../../components/carousel/CreateCarousel';
import CarouselItem from '../../components/carousel/CarouselItem';
// import SearchInput from '../../components/filters/SearchInput';
// import RefreshButton from '../../components/filters/RefreshButton';

interface FormData {
  order: number;
}

const Carousel: React.FC = () => {
  const { search } = useLocation();
  const { loading } = useAppSelector((state) => state.carousel);
  const [carousels, setCarousels] = useState<CarouselType[]>([]);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const carouselRequest = useCarouselRequest();
  // const [ searchText,setSearchText ] = useState<string>("")

  
  const fetchData = useCallback(async () => {
    try {
      const dispatchData = await carouselRequest({ method: 'GET', data: null, id: null });
      if (dispatchData.meta.requestStatus === 'fulfilled' && dispatchData.payload.data) {
        const data = dispatchData.payload.data;
        setCarousels(data);
      }
    } catch (error) {
      console.error('Failed to fetch carousels:', error);
    }
  }, [carouselRequest]);

  
  const onSubmit = useCallback(
    async (data: FormData, id: number) => {
      try {
        const dispatchData = await carouselRequest({ method: 'PUT', data, id });
        if (dispatchData.meta.requestStatus === 'fulfilled') {
          await fetchData();
        }
      } catch (error) {
        console.error('Failed to submit carousel data:', error);
      }
    },
    [carouselRequest, fetchData]
  );

  
  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await carouselRequest({ method: 'DELETE', data: null, id });
        fetchData();
      } catch (error) {
        console.error('Failed to delete carousel:', error);
      }
    },
    [carouselRequest, fetchData]
  );

  
  const handleChangeActive = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
      try {
        await carouselRequest({ method: 'PUT', data: { active: event.target.checked }, id });
        await fetchData();
      } catch (error) {
        console.error('Failed to update carousel:', error);
      }
    },
    [carouselRequest, fetchData]
  );

  useEffect(() => {
    fetchData();
  }, [search, fetchData]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
      {loading && <Loading />}
      <Typography variant="h5" sx={{ mb: 3 }}>
        Carousel List
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Add New
        </Button>
        <SiteDialog
          open={createOpen}
          setOpen={setCreateOpen}
          dialogChildren={<CreateCarousel setDialogOpen={setCreateOpen} refreshCarousels={fetchData} />}
        />
      </Box>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                No
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                Image
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                Filename
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                Order
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                Active
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carousels.length > 0 ? (
              carousels.map((carousel, index) => (
                <CarouselItem
                  key={carousel.id}
                  carousel={carousel}
                  index={index}
                  onSubmit={onSubmit}
                  handleDelete={handleDelete}
                  handleChangeActive={handleChangeActive}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No carousels found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default Carousel;
